import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  ArrowLeft, Coins, Mail, Trash2, Send, Loader2, UserPlus, RefreshCw, Wallet, Users,
} from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminStakeholders() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const confirm = useConfirm();
  const [tab, setTab] = useState('invitations');
  const [invitations, setInvitations] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', membership_amount: 0 });
  const [submitting, setSubmitting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, m] = await Promise.all([
        axios.get(`${API}/admin/stakeholders/invitations`, { headers }),
        axios.get(`${API}/admin/stakeholders/members`, { headers }),
      ]);
      setInvitations(i.data.invitations || []);
      setMembers(m.data.members || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async () => {
    if (!inviteForm.email.trim()) return toast.error('Email required');
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/stakeholders/invitations`, {
        email: inviteForm.email,
        name: inviteForm.name,
        membership_amount_cents: Math.round((parseFloat(inviteForm.membership_amount) || 0) * 100),
      }, { headers });
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setShowInvite(false);
      setInviteForm({ email: '', name: '', membership_amount: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  const resendInvite = async (id) => {
    try {
      await axios.post(`${API}/admin/stakeholders/invitations/${id}/resend`, {}, { headers });
      toast.success('Reminder sent');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteInvite = async (inv) => {
    const ok = await confirm({ title: `Delete invitation for ${inv.email}?`, body: 'They will no longer be able to accept this link.', confirmText: 'Delete' });
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/stakeholders/invitations/${inv.invitation_id}`, { headers });
      toast.success('Invitation deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const copyLink = async (inv) => {
    const url = `${window.location.origin}/stakeholder/invite/${inv.token}`;
    try { await navigator.clipboard.writeText(url); toast.success('Invite link copied'); }
    catch { toast.error('Could not copy'); }
  };

  const fmt = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const statusBadge = (s) => {
    const m = {
      pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
      accepted: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
      expired: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
      declined: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    };
    return <Badge variant="outline" className={`${m[s] || m.pending} text-[10px]`}>{s}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="mb-2" data-testid="stakeholders-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
        </Button>
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2 font-['Space_Grotesk']">
              <Coins className="w-7 h-7 text-fuchsia-400" /> Stakeholders
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Invite-only profit-share members. Comp memberships are free by default — no $3,000 buy-in required.</p>
          </div>
          <Button onClick={() => setShowInvite(true)} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid="invite-stakeholder-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Invite stakeholder
          </Button>
        </div>

        <div className="flex gap-2 mb-4">
          <Button variant={tab === 'invitations' ? 'default' : 'outline'} size="sm" onClick={() => setTab('invitations')} data-testid="tab-invitations">
            Invitations · {invitations.length}
          </Button>
          <Button variant={tab === 'members' ? 'default' : 'outline'} size="sm" onClick={() => setTab('members')} data-testid="tab-members">
            <Users className="w-4 h-4 mr-1.5" /> Members · {members.length}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
        ) : tab === 'invitations' ? (
          invitations.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">No invitations yet. Send the first one above.</div>
          ) : (
            <div className="space-y-2" data-testid="invitations-list">
              {invitations.map((inv) => (
                <Card key={inv.invitation_id} data-testid={`invitation-row-${inv.invitation_id}`}>
                  <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                    <Mail className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{inv.name || inv.email}</span>
                        {statusBadge(inv.status)}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{inv.email} · invited {new Date(inv.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => copyLink(inv)} data-testid={`copy-link-${inv.invitation_id}`}>Copy link</Button>
                      {inv.status === 'pending' && <Button size="sm" variant="outline" onClick={() => resendInvite(inv.invitation_id)} data-testid={`resend-${inv.invitation_id}`}><Send className="w-3.5 h-3.5 mr-1" /> Resend</Button>}
                      {inv.status !== 'accepted' && <Button size="sm" variant="destructive" onClick={() => deleteInvite(inv)} data-testid={`delete-invite-${inv.invitation_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        ) : (
          members.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">No active stakeholders yet.</div>
          ) : (
            <div className="space-y-2" data-testid="members-list">
              {members.map((m) => (
                <Card key={m.user_id} data-testid={`member-row-${m.user_id}`}>
                  <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                    <Wallet className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{m.name || m.email}</div>
                      <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                    </div>
                    <div className="flex gap-6 text-sm">
                      <div><div className="text-[10px] uppercase text-muted-foreground">Balance</div><div className="font-semibold text-emerald-400">{fmt(m.wallet_balance_cents)}</div></div>
                      <div><div className="text-[10px] uppercase text-muted-foreground">Earned</div><div className="font-semibold">{fmt(m.lifetime_earned_cents)}</div></div>
                      <div><div className="text-[10px] uppercase text-muted-foreground">Holdings</div><div className="font-semibold">{m.active_holdings}</div></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        )}

        <Dialog open={showInvite} onOpenChange={setShowInvite}>
          <DialogContent data-testid="invite-dialog">
            <DialogHeader>
              <DialogTitle>Invite a stakeholder</DialogTitle>
              <DialogDescription>They'll receive an email with a magic link to activate their stakeholder account. Leave the amount at $0 for a free comp membership.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Email *</label>
                <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="backer@example.com" data-testid="invite-email-input" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Name (optional)</label>
                <Input value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} placeholder="Their full name" data-testid="invite-name-input" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Membership amount (USD)</label>
                <Input type="number" min="0" step="100" value={inviteForm.membership_amount} onChange={(e) => setInviteForm({ ...inviteForm, membership_amount: e.target.value })} placeholder="0 for free comp membership" data-testid="invite-amount-input" />
                <div className="flex gap-2 flex-wrap mt-2">
                  {[0, 500, 1000, 3000, 5000].map((v) => (
                    <Button key={v} size="sm" variant="outline" type="button" onClick={() => setInviteForm({ ...inviteForm, membership_amount: v })}>
                      {v === 0 ? 'Free' : `$${v.toLocaleString()}`}
                    </Button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  $0 = free comp membership (skips Stripe, instant account). Any amount &gt; $0 sends them to Stripe Checkout. Stripe fee auto-grossed-up.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
              <Button onClick={sendInvite} disabled={submitting} data-testid="send-invite-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Send invite
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Button variant="ghost" size="sm" onClick={load} className="mt-6 text-muted-foreground" data-testid="refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>
    </div>
  );
}
